import { differenceInMilliseconds } from 'date-fns';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { FormikProvider, useFormik } from 'formik';
import { useId, useMemo, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import * as yup from 'yup';
import CurrencyInput from '@/modules/common/components/CurrencyInput.tsx';
import { useRegisterTypeStrings } from '@/modules/common/hooks/useRegisterTypeStrings.tsx';
import { preventFormDefault } from '@/modules/common/utils/preventFormDefault.ts';
import { AddNewNavBody } from '@/modules/issuer-dashboard/components/portfolios/IssuerAddNewNavModal.tsx';
import { useNavCreateMutation } from '@/modules/issuer-dashboard/queries/nav.tsx';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  CalendarInputProps,
  ModalContentWrapper,
  TabButtons,
  TabItem,
  Tabs,
  TextInputField,
} from 'ui';
import { CreateNavRequest } from 'api';

dayjs.extend(utc);
//
export const validationSchema = yup.object({
  changes_date: yup.string().trim().required('This field is required'),
  price_per_share: yup
    .number()
    .typeError('Price must be a number')
    .required('This field is required'),
  justification: yup.string().nullable(),
});

enum TabName {
  ManualInput = 'Manual input',
  CalculateWithPortfolioValue = 'Calculate with portfolio value',
}

const initialValues = {
  changes_date: '',
  price_per_share: 1,
  register_class_id: 0,
  justification: '',
};

export interface IssuerAddNavModalProps extends BaseModalProps {
  register_class_id: number;
  activeDate: Date;
  defaultPricePerShare?: number;
}

export const IssuerAddNavModal = ({
  open,
  onClose,
  register_class_id,
  activeDate,
  defaultPricePerShare,
}: IssuerAddNavModalProps) => {
  const disabledTooltipId = useId();
  const getRegisterTypeStrings = useRegisterTypeStrings();
  const [activeTab, setActiveTab] = useState<TabName>(TabName.ManualInput);

  const tabItems = useMemo<TabItem<TabName>[]>(
    () => [
      {
        name: TabName.ManualInput,
        container: (
          <ManualInputBlock
            register_class_id={register_class_id}
            onClose={onClose}
            activeDate={activeDate}
            defaultPricePerShare={defaultPricePerShare}
          />
        ),
      },
      {
        name: TabName.CalculateWithPortfolioValue,
        container: <AddNewNavBody fromPortfolio={false} onSuccess={onClose} />,
      },
    ],
    [activeDate, defaultPricePerShare, onClose, register_class_id],
  );

  return (
    <ModalContentWrapper
      open={open}
      title={`Update ${getRegisterTypeStrings('nav')}`}
      onClose={onClose}
    >
      <div className="w-full flex flex-col space-y-8">
        <TabButtons
          tabs={tabItems}
          setActiveTab={setActiveTab}
          activeTab={activeTab}
          disabledTooltipId={disabledTooltipId}
          className="w-full flex items-center justify-start space-x-6 relative border-b border-gray-200 pb-[12px]"
        />
        <Tabs activeTab={activeTab} items={tabItems} />
      </div>
      <Tooltip id={disabledTooltipId} />
    </ModalContentWrapper>
  );
};

interface ManualInputBlockProps {
  register_class_id: number;
  activeDate: Date;
  onClose: () => void;
  defaultPricePerShare?: number;
}

const ManualInputBlock = ({
  onClose,
  register_class_id,
  activeDate,
  defaultPricePerShare,
}: ManualInputBlockProps) => {
  const formik = useFormik<CreateNavRequest>({
    initialValues: {
      ...initialValues,
      price_per_share: defaultPricePerShare ?? initialValues.price_per_share,
      register_class_id,
      changes_date: dayjs().utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]'), //format(new Date(), "yyyy-MM-dd'T'HH:mm:ss"),
    },
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    onSubmit: async values => {
      await addNav(values);
      onClose();
    },
    // enableReinitialize: true,
  });

  const isActualAndHistoricalPrice = useMemo(() => {
    return differenceInMilliseconds(activeDate, new Date(formik.values.changes_date)) >= 0;
  }, [activeDate, formik.values.changes_date]);

  const { mutateAsync: addNav, isLoading } = useNavCreateMutation({
    setErrors: formik.setErrors,
  });

  const handleChangeDate: CalendarInputProps['onChange'] = date => {
    if (!date) {
      formik.setFieldValue('changes_date', '');
      formik.setFieldError('changes_date', '');
      return;
    }

    const currentDate = date.utc().format('YYYY-MM-DDTHH:mm:ss.SSSSSS[+00:00]');

    formik.setFieldValue('changes_date', currentDate);
    formik.setFieldError('changes_date', '');
  };

  const handleChangePricePerShare = (nav: number | string) => {
    formik.setFieldValue('price_per_share', nav);
    formik.setFieldError('price_per_share', '');
  };

  return (
    <FormikProvider value={formik}>
      <form className="w-full flex flex-col space-y-10" onSubmit={preventFormDefault}>
        <div className="w-full flex flex-col space-y-8">
          {isActualAndHistoricalPrice && (
            <span className="text-sm leading-[22px] font-[Inter]">
              Please add your justification (will be shown in Update Log and Investor notifications)
            </span>
          )}
          <CalendarInput
            label="Active From"
            name="changes_date"
            showTime={false}
            format="DD-MM-YYYY"
            onChange={handleChangeDate}
            value={formik.values.changes_date ? dayjs(formik.values.changes_date) : undefined}
            error={formik.errors.changes_date}
          />
          <CurrencyInput
            name="price_per_share"
            label="Price per share/unit"
            value={formik.values.price_per_share}
            error={formik.errors.price_per_share}
            onChange={handleChangePricePerShare}
            decimalPoints={4}
          />
          {isActualAndHistoricalPrice && (
            <TextInputField
              name="justification"
              label="Your justification for historical NAV change"
              placeholder="Enter your text"
              textarea={true}
            />
          )}
        </div>
        <Button onClick={formik.handleSubmit} loading={isLoading}>
          Submit
        </Button>
      </form>
    </FormikProvider>
  );
};
